/**
 * Created by Ning Jiang on 2021-06-23.
 */

import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap, TimelineLite} from "gsap";
import {Serial} from "../../Serial";
import {Layers, SerialLayers} from "../../view/SerialLayers";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {ReelPositionController} from "../../reels/ReelPositionController";
import {SerialGameAssets} from "../../SerialGameAssets";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {WaysWonTextContainer} from "../presentations/initial/WaysWonTextContainer";

export class IndividualEffect extends PIXI.Container {

    public readonly symbolHeight:number;
    private _frame:PIXI.Sprite;
    private _effect:TimelineSprite;
    private _overlay:PIXI.Container;

    constructor(symHeight:number) {
        super();

        this.symbolHeight = symHeight;

        this._overlay = new PIXI.Container();
        this.addChild(this._overlay);

        const left = new PIXI.Sprite(Serial.assetLoader.getTextures(`individualwin/oneSide`)[0]);
        left.anchor.set(0, 0.5);
        left.height = symHeight;
        left.position.set(-ReelPositionController.DEFAULT_SYMBOL_WIDTH * 0.5, 0);
        this._overlay.addChild(left);

        const right = new PIXI.Sprite(Serial.assetLoader.getTextures(`individualwin/oneSide`)[0]);
        right.anchor.set(0, 0.5);
        right.height = symHeight;
        right.scale.x = -1;
        right.position.set(ReelPositionController.DEFAULT_SYMBOL_WIDTH * 0.5, 0);
        this._overlay.addChild(right);

        const textureName = this.symbolHeight > 150 ? `individualWin/156px/156px_` : `individualWin/104px/104px_`;
        this._effect = new TimelineSprite(Serial.assetLoader.getTextures(textureName), 30);
        this._effect.anchor.set(0.5);
        this._effect.blendMode = PIXI.BLEND_MODES.ADD;
        if(this.symbolHeight < 100) {
            this._effect.height = this.symbolHeight;
        }
        this.addChild(this._effect);

        this._frame = new PIXI.Sprite(Serial.assetLoader.getTextures(this.getFrameTexture())[0]);
        this._frame.anchor.set(0.5);
        this._frame.blendMode = PIXI.BLEND_MODES.ADD;
        this.addChild(this._frame);

        this.reset();
    }

    private getFrameTexture():string {
        if(this.symbolHeight > 150) {
            return `individualWin/frameGreen156px`;
        }
        if(this.symbolHeight > 100) {
            return `individualWin/frameGreen104px`;
        }
        return `individualWin/frameGreen78px`;
    }

    public play(level:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(<gsap.core.Timeline><unknown>this._effect.getAnimationAutoShowHide(true, true, this.getLevelFrames(level)));
        const duration = tl.totalDuration();
        tl.add(this.getEffectAlphaTl(level, duration), 0);
        tl.add(this.getOverlayTl(level, duration), 0);
        tl.add(this.getFrameTl(level, duration), 0);
        return tl;
    }

    private getLevelFrames(level:number):([number, number] | undefined) {
        switch(level) {
            case 0:
                return [0, 8];
            case 1:
                return [0, 28];
            default:
                return undefined;
        }
    }

    private getEffectAlphaTl(level:number, duration:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        switch(level) {
            case 0:
                tl.add(() => {this._effect.alpha = 0.55});
                tl.add(gsap.to(this._effect, {alpha : 0, duration : duration}));
                break;
            case 1:
                tl.add(() => {this._effect.alpha = 0.55});
                tl.add(gsap.to(this._effect, {alpha : 0, duration : duration - 0.6}), 0.6);
                break;
            case 2:
                tl.add(() => {this._effect.alpha = 0.6});
                break;
            default:
                tl.add(() => {this._effect.alpha = 1});

        }
        return tl;
    }

    private getOverlayTl(level:number, duration:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this._overlay.visible = true;
        });
        switch(level) {
            case 0:
                break;
            case 1:
                tl.add(gsap.to(this._overlay, {alpha : 0.4, duration : 0.2}));
                tl.add(gsap.to(this._overlay, {alpha : 0, duration : duration - 0.2}));
                break;
            case 2:
                tl.add(gsap.to(this._overlay, {alpha : 0.7, duration : 0.2}));
                tl.add(gsap.to(this._overlay, {alpha : 0, duration : duration - 0.2}));
                break;
            default:
                tl.add(gsap.to(this._overlay, {alpha : 1, duration : 0.2}));
                tl.add(gsap.to(this._overlay, {alpha : 0, duration : duration - 0.2}));
        }
        return tl;
    }

    private getFrameTl(level:number, duration:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this._frame.visible = true;
        });
        switch(level) {
            case 0:
            case 1:
                break;
            case 2:
                tl.add(gsap.to(this._frame, {alpha : 0.3, duration : 0.2}));
                tl.add(gsap.to(this._frame, {alpha : 0, duration : duration - 0.2}));
                break;
            default:
                tl.add(gsap.to(this._frame, {alpha : 1, duration : 0.2}));
                tl.add(gsap.to(this._frame, {alpha : 0, duration : duration - 0.2}));
        }
        return tl;
    }

    public reset():void {
        this._effect.hide();
        this._overlay.alpha = 0;
        this._overlay.visible = false;
        this._frame.alpha = 0;
        this._frame.visible = false;
    }
}

export class IndividualWinText extends PIXI.Container {

    private _maxWidth:number = 280;

    constructor(winAmount:number, way:number) {
        super();
        // winAmount = 10000000000000;
        // way = 11216;
        const winNumber = new PIXI.BitmapText(`${Serial.apiPlugin.currency.formatValue(winAmount)}`, {
            fontName : SerialGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize : 20,
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        winNumber.letterSpacing = -10;
        (<PIXI.Point>winNumber.anchor).set(0.5, 0);
        winNumber.y = -20;
        this.addChild(winNumber);
        winNumber.updateTransform();
        if(winNumber.width > this._maxWidth) {
            const scale = MathHelper.roundToDecimals(this._maxWidth / winNumber.width, 2);
            winNumber.scale.set(scale);
        }

        const waysWon:WaysWonTextContainer = new WaysWonTextContainer();
        waysWon.show(way);
        waysWon.scale.set(0.4);
        waysWon.y = 30;
        this.addChild(waysWon);
    }
}

export class IndividualWinView {

    private _layer:StageLayer;
    private _effectPool:IndividualEffect[][] = [[], [], []]; // 156, 104 and 78
    private _playingPool:IndividualEffect[] = [];
    private _winText:IndividualWinText | null;
    private _winTextTl:TweenLite | null;

    private _stopped:boolean;

    constructor() {
        this._layer = SerialLayers.getLayer(Layers.WIN_PRESENTATIONS)!;
    }

    public playAnimation(symHeight:number, position:PIXI.Point, effectLevel:number, showWinText:boolean, winAmount?:number, winWays?:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        if(this._stopped) {
            return tl;
        }

        const effect = this.getEffect(symHeight);
        effect.position.set(position.x, position.y);
        this._playingPool.push(effect);

        tl.add([
            () => {
                if(!this._stopped) {
                    this._layer.addChild(effect);
                }
            },
            effect.play(effectLevel)
        ], 0);
        if(showWinText) {
            tl.add(() => {
                this.removeWinText();
                if(this._stopped) {
                    return;
                }
                this._winText = new IndividualWinText(winAmount!, winWays!);
                this._winText.position.set(position.x, position.y);
                this._winText.alpha = 0;
                this._layer.addChild(this._winText!);
                this._winTextTl = gsap.to(this._winText, {alpha : 1, duration : 0.1});
            }, 0);
        }

        tl.add(() => {this.recycleEffect(effect);});
        return tl;
    }

    private getEffect(symHeight:number):IndividualEffect {
        const pool = this.getEffectPool(symHeight);
        if(pool.length > 0) {
            return pool.pop()!;
        }

        return new IndividualEffect(symHeight);
    }

    private getEffectPool(symHeight:number):IndividualEffect[] {
        if(symHeight > 150) {
            return this._effectPool[0];
        }
        if(symHeight > 100) {
            return this._effectPool[1];
        }
        return this._effectPool[2];
    }

    private recycleEffect(effect:IndividualEffect):void {
        ArrayHelper.removeFirstMatchElement(this._playingPool, effect);
        if(effect.parent) {
            effect.parent.removeChild(effect);
        }
        effect.reset();
        const pool = this.getEffectPool(effect.symbolHeight);
        if(pool.indexOf(effect) == -1) {
            pool.push(effect);
        }
    }

    public start():void {
        this._stopped = false;
    }

    public stop():void {
        this._stopped = true;
        while(this._playingPool.length > 0) {
            this.recycleEffect(this._playingPool[0]);
        }
        this.removeWinText();
    }

    public removeWinText():void {
        if(this._winTextTl && this._winTextTl.isActive()) {
            this._winTextTl.progress(1);
        }
        this._winTextTl = null;

        if(this._winText) {
            if(this._winText.parent) {
                this._winText.parent.removeChild(this._winText);
            }
            this._winText.destroy();
            this._winText = null;
        }
    }
}